import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, User, DoorOpen, Users, CheckCircle2, AlertTriangle } from "lucide-react";

const BED_SHARING = ["Single", "Double Sharing", "Triple Sharing", "Quad Sharing"];
const CAPACITY_MAP = { "Single": 1, "Double Sharing": 2, "Triple Sharing": 3, "Quad Sharing": 4 };

export default function RoomsTab() {
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/rooms"),
        api.get("/students"),
      ]);
      setRooms(r.data || []);
      setStudents(s.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Merge: for each declared room, find occupants by matching flat_number
  const roomView = useMemo(() => {
    const view = rooms.map((room) => {
      const occupants = students.filter((s) => (s.flat_number || "").trim() === room.room_number.trim());
      return { ...room, occupants };
    });
    // Include unregistered rooms (students with a flat_number not in rooms)
    const declared = new Set(rooms.map((r) => r.room_number.trim()));
    const orphans = new Map();
    for (const s of students) {
      const f = (s.flat_number || "").trim();
      if (!f || declared.has(f)) continue;
      if (!orphans.has(f)) orphans.set(f, []);
      orphans.get(f).push(s);
    }
    for (const [f, occs] of orphans) {
      view.push({
        id: `orphan-${f}`,
        room_number: f,
        sub_rooms: [],
        bed_capacity: occs.length,
        bed_sharing: "",
        notes: "",
        orphan: true,
        occupants: occs,
      });
    }
    return view.sort((a, b) => a.room_number.localeCompare(b.room_number, undefined, { numeric: true }));
  }, [rooms, students]);

  const totalRooms = roomView.length;
  const totalBeds = roomView.reduce((n, r) => n + (r.bed_capacity || 1), 0);
  const occupied = roomView.reduce((n, r) => n + r.occupants.length, 0);
  const available = Math.max(totalBeds - occupied, 0);

  const remove = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await api.delete(`/rooms/${id}`);
      toast.success("Room deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="rooms-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Stat label="Rooms" value={totalRooms} bg="#FFE600" fg="#0A0A0A" icon={DoorOpen} />
        <Stat label="Total Beds" value={totalBeds} bg="#00E5FF" fg="#0A0A0A" icon={Users} />
        <Stat label="Occupied" value={occupied} bg="#00CC66" fg="#FFFFFF" icon={CheckCircle2} />
        <Stat label="Available" value={available} bg="#FF4D4D" fg="#FFFFFF" icon={AlertTriangle} />
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-black text-2xl tracking-tighter">Manage rooms</h3>
          <p className="text-sm font-medium text-[#4A4A4A]">
            Create, edit, or remove rooms. Set sub-rooms and bed capacity for each. Existing students remain
            linked by room number.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          data-testid="add-room-button"
          className="inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
        >
          <Plus size={16} /> Add Room
        </button>
      </div>

      {loading && <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">Loading…</div>}
      {!loading && roomView.length === 0 && (
        <div className="brutal-card p-8 text-center font-medium text-[#4A4A4A]">
          No rooms yet. Add the first room to build the allotment map.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {roomView.map((room) => {
          const capacity = room.bed_capacity || 1;
          const full = room.occupants.length >= capacity;
          const empty = room.occupants.length === 0;
          const badgeBg = empty ? "#FFFFFF" : full ? "#FF4D4D" : "#00CC66";
          const badgeFg = empty ? "#0A0A0A" : "#FFFFFF";
          return (
            <div
              key={room.id}
              data-testid={`room-card-${room.room_number}`}
              className="brutal-card p-5 bg-white"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest">
                    Room {room.orphan && <span className="text-[#FF4D4D]">· unregistered</span>}
                  </div>
                  <h4 className="font-heading font-black text-3xl tracking-tighter mt-1">{room.room_number}</h4>
                  {room.bed_sharing && (
                    <div className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A] mt-1">
                      {room.bed_sharing}
                    </div>
                  )}
                </div>
                <span
                  style={{ backgroundColor: badgeBg, color: badgeFg }}
                  className="px-3 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold"
                >
                  {room.occupants.length}/{capacity} {full ? "FULL" : empty ? "EMPTY" : "AVAILABLE"}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {Array.from({ length: Math.max(capacity, room.occupants.length) }, (_, i) => {
                  const p = room.occupants[i];
                  return (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center"
                      style={{ backgroundColor: p ? "#0A0A0A" : "#F8F9FA", color: p ? "#FFE600" : "#4A4A4A" }}
                      title={p ? `${p.full_name} (${p.student_id_number})` : "Empty bed"}
                    >
                      <User size={18} strokeWidth={2.5} />
                    </div>
                  );
                })}
              </div>

              {room.sub_rooms && room.sub_rooms.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {room.sub_rooms.map((sr) => (
                    <span key={sr} className="text-[10px] font-bold uppercase px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600]">
                      {sr}
                    </span>
                  ))}
                </div>
              )}

              {room.notes && (
                <div className="mt-3 text-xs font-medium text-[#4A4A4A] italic">"{room.notes}"</div>
              )}

              {room.occupants.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm">
                  {room.occupants.map((o) => (
                    <li key={o.id} className="flex justify-between gap-2">
                      <span className="font-bold truncate">{o.full_name}</span>
                      <span className="text-xs font-medium text-[#4A4A4A]">
                        {o.student_id_number}{o.sub_room ? ` · ${o.sub_room}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {!room.orphan && (
                <div className="mt-4 pt-3 border-t-2 border-[#0A0A0A] flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setEditing(room);
                      setDialogOpen(true);
                    }}
                    data-testid={`edit-room-${room.id}`}
                    className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] hover:shadow-brutal-sm transition-shadow"
                    title="Edit"
                  >
                    <Edit3 size={14} strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={() => remove(room.id)}
                    data-testid={`delete-room-${room.id}`}
                    className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
                    title="Delete"
                  >
                    <Trash2 size={14} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <RoomDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        room={editing}
        onSaved={load}
      />
    </div>
  );
}

function Stat({ label, value, bg, fg, icon: Icon }) {
  return (
    <div
      style={{
        backgroundColor: bg, color: fg,
        border: "2px solid #0A0A0A", borderRadius: "0.75rem",
        boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)", padding: "1.25rem",
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em]">{label}</span>
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-black text-3xl tracking-tighter mt-2">{value}</div>
    </div>
  );
}

function RoomDialog({ open, onOpenChange, room, onSaved }) {
  const [form, setForm] = useState({ room_number: "", sub_rooms: "", bed_capacity: 1, bed_sharing: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (room) {
      setForm({
        room_number: room.room_number || "",
        sub_rooms: (room.sub_rooms || []).join(", "),
        bed_capacity: room.bed_capacity || 1,
        bed_sharing: room.bed_sharing || "",
        notes: room.notes || "",
      });
    } else {
      setForm({ room_number: "", sub_rooms: "", bed_capacity: 1, bed_sharing: "", notes: "" });
    }
  }, [room, open]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        room_number: form.room_number.trim(),
        sub_rooms: form.sub_rooms.split(",").map((s) => s.trim()).filter(Boolean),
        bed_capacity: Number(form.bed_capacity) || 1,
        bed_sharing: form.bed_sharing || null,
        notes: form.notes || "",
      };
      if (room) {
        const { room_number, ...upd } = payload;
        await api.put(`/rooms/${room.id}`, { ...upd, room_number });
        toast.success("Room updated");
      } else {
        await api.post("/rooms", payload);
        toast.success("Room added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white" data-testid="room-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-3xl tracking-tighter">
            {room ? "Edit Room" : "Add Room"}
          </DialogTitle>
          <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
            Configure the room number, optional sub-rooms, bed capacity and sharing type. Notes are for admin reference only.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <label className="block md:col-span-2">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Room Number <span className="text-[#FF4D4D]">*</span></span>
            <input data-testid="room-form-number" value={form.room_number}
              onChange={(e) => change("room_number", e.target.value)} className="input-brutal"
              placeholder="e.g. A-101" required />
          </label>
          <label className="block md:col-span-2">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Sub Rooms (comma-separated)</span>
            <input data-testid="room-form-subs" value={form.sub_rooms}
              onChange={(e) => change("sub_rooms", e.target.value)} className="input-brutal"
              placeholder="e.g. A, B, C" />
          </label>
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Bed Capacity</span>
            <input data-testid="room-form-capacity" type="number" min={1} value={form.bed_capacity}
              onChange={(e) => change("bed_capacity", e.target.value)} className="input-brutal" />
          </label>
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Bed Sharing</span>
            <Select value={form.bed_sharing} onValueChange={(v) => {
              change("bed_sharing", v);
              if (CAPACITY_MAP[v]) change("bed_capacity", CAPACITY_MAP[v]);
            }}>
              <SelectTrigger data-testid="room-form-sharing" className="input-brutal font-bold">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {BED_SHARING.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block md:col-span-2">
            <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">Notes</span>
            <textarea data-testid="room-form-notes" value={form.notes}
              onChange={(e) => change("notes", e.target.value)} className="input-brutal" rows={2}
              placeholder="e.g. AC room, balcony, corner unit" />
          </label>
        </div>
        <DialogFooter className="mt-6 gap-3">
          <button onClick={() => onOpenChange(false)}
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.room_number.trim()} data-testid="room-form-save"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60">
            {saving ? "Saving..." : room ? "Save changes" : "Add room"}
          </button>
        </DialogFooter>
        <style>{`
          .input-brutal { width: 100%; padding: 0.7rem 1rem; border: 2px solid #0A0A0A; border-radius: 0.75rem; background: white; font-weight: 500; }
          .input-brutal:focus { outline: none; box-shadow: 0 0 0 2px white, 0 0 0 4px #0A0A0A; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
